/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { BuildTracebackUrlInput } from '../models/BuildTracebackUrlInput';
import type { BuildTracebackUrlOutput } from '../models/BuildTracebackUrlOutput';
import type { CovenantReportingStatusesInput } from '../models/CovenantReportingStatusesInput';
import type { CovenantReportingStatusesOutput } from '../models/CovenantReportingStatusesOutput';
import type { ListClusterTeamInput } from '../models/ListClusterTeamInput';
import type { ListClusterTeamOutput } from '../models/ListClusterTeamOutput';
import type { ListDashboardsInput } from '../models/ListDashboardsInput';
import type { ListDashboardsOutput } from '../models/ListDashboardsOutput';
import type { ListDimensionsInput } from '../models/ListDimensionsInput';
import type { ListDimensionsOutput } from '../models/ListDimensionsOutput';
import type { ListEntitiesInput } from '../models/ListEntitiesInput';
import type { ListEntitiesOutput } from '../models/ListEntitiesOutput';
import type { ListEntityTeamInput } from '../models/ListEntityTeamInput';
import type { ListEntityTeamOutput } from '../models/ListEntityTeamOutput';
import type { ListExcelFunctionsInput } from '../models/ListExcelFunctionsInput';
import type { ListExcelFunctionsOutput } from '../models/ListExcelFunctionsOutput';
import type { ListNamespaceRelationshipsInput } from '../models/ListNamespaceRelationshipsInput';
import type { ListNamespaceRelationshipsOutput } from '../models/ListNamespaceRelationshipsOutput';
import type { ListNamespacesInput } from '../models/ListNamespacesInput';
import type { ListNamespacesOutput } from '../models/ListNamespacesOutput';
import type { ListRelatedEntitiesInput } from '../models/ListRelatedEntitiesInput';
import type { ListRelatedEntitiesOutput } from '../models/ListRelatedEntitiesOutput';
import type { ListResourcesInput } from '../models/ListResourcesInput';
import type { ListResourcesOutput } from '../models/ListResourcesOutput';
import type { ListSchemaGroupsInput } from '../models/ListSchemaGroupsInput';
import type { ListSchemaGroupsOutput } from '../models/ListSchemaGroupsOutput';
import type { ListSchemaPropertiesInput } from '../models/ListSchemaPropertiesInput';
import type { ListSchemaPropertiesOutput } from '../models/ListSchemaPropertiesOutput';
import type { McpAllowedClustersOutput } from '../models/McpAllowedClustersOutput';
import type { McpControlPlaneManifestOutput } from '../models/McpControlPlaneManifestOutput';
import type { McpControlPlaneResourceReadInput } from '../models/McpControlPlaneResourceReadInput';
import type { McpControlPlaneResourceReadOutput } from '../models/McpControlPlaneResourceReadOutput';
import type { McpControlPlaneResourcesOutput } from '../models/McpControlPlaneResourcesOutput';
import type { McpSessionExchangeInput } from '../models/McpSessionExchangeInput';
import type { McpSessionExchangeOutput } from '../models/McpSessionExchangeOutput';
import type { McpSessionRefreshInput } from '../models/McpSessionRefreshInput';
import type { McpSessionRevokeInput } from '../models/McpSessionRevokeInput';
import type { ReadDashboardInput } from '../models/ReadDashboardInput';
import type { ReadDashboardOutput } from '../models/ReadDashboardOutput';
import type { ReadResourceInput } from '../models/ReadResourceInput';
import type { ReadResourceOutput } from '../models/ReadResourceOutput';
import type { ReadTracebackOutput } from '../models/ReadTracebackOutput';
import type { RenderTableInput } from '../models/RenderTableInput';
import type { RenderTableOutput } from '../models/RenderTableOutput';
import type { ReportingEntityStatusInput } from '../models/ReportingEntityStatusInput';
import type { ReportingEntityStatusOutput } from '../models/ReportingEntityStatusOutput';
import type { ReportingPeriodSummaryInput } from '../models/ReportingPeriodSummaryInput';
import type { ReportingPeriodSummaryOutput } from '../models/ReportingPeriodSummaryOutput';
import type { ReportingReminderTargetsInput } from '../models/ReportingReminderTargetsInput';
import type { ReportingReminderTargetsOutput } from '../models/ReportingReminderTargetsOutput';
import type { ResolveLxInput } from '../models/ResolveLxInput';
import type { ResolveLxOutput } from '../models/ResolveLxOutput';
import type { SearchPropertiesInput } from '../models/SearchPropertiesInput';
import type { SearchPropertiesOutput } from '../models/SearchPropertiesOutput';
import type { TableQueryGuideInput } from '../models/TableQueryGuideInput';
import type { TableQueryGuideOutput } from '../models/TableQueryGuideOutput';
import type { ValidateExcelFormulasInput } from '../models/ValidateExcelFormulasInput';
import type { ValidateExcelFormulasOutput } from '../models/ValidateExcelFormulasOutput';
import type { ValidateLxInput } from '../models/ValidateLxInput';
import type { ValidateLxOutput } from '../models/ValidateLxOutput';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class McpService {

    constructor(public readonly httpRequest: BaseHttpRequest) {}

    /**
     * Exchange Control Plane Session
     * @param requestBody
     * @returns McpSessionExchangeOutput Successful Response
     * @throws ApiError
     */
    public exchangeControlPlaneSession(
        requestBody: McpSessionExchangeInput,
    ): CancelablePromise<McpSessionExchangeOutput> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/mcp/control-plane/session/exchange',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Control Plane Session Clusters
     * @returns McpAllowedClustersOutput Successful Response
     * @throws ApiError
     */
    public listControlPlaneSessionClusters(): CancelablePromise<McpAllowedClustersOutput> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/mcp/control-plane/session/clusters',
        });
    }

    /**
     * Refresh Control Plane Session
     * @param requestBody
     * @returns McpSessionExchangeOutput Successful Response
     * @throws ApiError
     */
    public refreshControlPlaneSession(
        requestBody: McpSessionRefreshInput,
    ): CancelablePromise<McpSessionExchangeOutput> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/mcp/control-plane/session/refresh',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Revoke Control Plane Session
     * @param requestBody
     * @returns void
     * @throws ApiError
     */
    public revokeControlPlaneSession(
        requestBody: McpSessionRevokeInput,
    ): CancelablePromise<void> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/mcp/control-plane/session/revoke',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Read Control Plane Manifest
     * @param xLumonicMcpControlPlaneAssertion
     * @returns McpControlPlaneManifestOutput Successful Response
     * @throws ApiError
     */
    public readControlPlaneManifest(
        xLumonicMcpControlPlaneAssertion?: (string | null),
    ): CancelablePromise<McpControlPlaneManifestOutput> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/mcp/control-plane/manifest',
            headers: {
                'X-Lumonic-MCP-Control-Plane-Assertion': xLumonicMcpControlPlaneAssertion,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Control Plane Resources
     * @param xLumonicMcpControlPlaneAssertion
     * @returns McpControlPlaneResourcesOutput Successful Response
     * @throws ApiError
     */
    public listControlPlaneResources(
        xLumonicMcpControlPlaneAssertion?: (string | null),
    ): CancelablePromise<McpControlPlaneResourcesOutput> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/mcp/control-plane/resources',
            headers: {
                'X-Lumonic-MCP-Control-Plane-Assertion': xLumonicMcpControlPlaneAssertion,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Read Cluster Control Plane Resource
     * @param clusterId
     * @param requestBody
     * @param xLumonicMcpToken
     * @param xLumonicMcpUserId
     * @param xLumonicMcpClientId
     * @param xLumonicMcpControlPlaneSessionId
     * @param xLumonicMcpControlPlaneAssertion
     * @param xLumonicMcpSessionToken
     * @returns McpControlPlaneResourceReadOutput Successful Response
     * @throws ApiError
     */
    public readClusterControlPlaneResource(
        clusterId: string,
        requestBody: McpControlPlaneResourceReadInput,
        xLumonicMcpToken?: (string | null),
        xLumonicMcpUserId?: (string | null),
        xLumonicMcpClientId?: (string | null),
        xLumonicMcpControlPlaneSessionId?: (string | null),
        xLumonicMcpControlPlaneAssertion?: (string | null),
        xLumonicMcpSessionToken?: (string | null),
    ): CancelablePromise<McpControlPlaneResourceReadOutput> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/mcp/control-plane/clusters/{cluster_id}/resources/read',
            path: {
                'cluster_id': clusterId,
            },
            headers: {
                'X-Lumonic-MCP-Token': xLumonicMcpToken,
                'X-Lumonic-MCP-User-Id': xLumonicMcpUserId,
                'X-Lumonic-MCP-Client-Id': xLumonicMcpClientId,
                'X-Lumonic-MCP-Control-Plane-Session-Id': xLumonicMcpControlPlaneSessionId,
                'X-Lumonic-MCP-Control-Plane-Assertion': xLumonicMcpControlPlaneAssertion,
                'X-Lumonic-MCP-Session-Token': xLumonicMcpSessionToken,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Read App Traceback
     * @param clusterId
     * @param entityId
     * @param effectiveDate
     * @param expression
     * @returns ReadTracebackOutput Successful Response
     * @throws ApiError
     */
    public readAppTraceback(
        clusterId: string,
        entityId: string,
        effectiveDate: string,
        expression: string,
    ): CancelablePromise<ReadTracebackOutput> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/mcp/app/traceback',
            query: {
                'cluster_id': clusterId,
                'entity_id': entityId,
                'effective_date': effectiveDate,
                'expression': expression,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Namespaces
     * List namespaces in the bound cluster so the model can understand entity grouping and schema boundaries.
     * @param clusterId
     * @param requestBody
     * @param xLumonicMcpToken
     * @param xLumonicMcpUserId
     * @param xLumonicMcpClientId
     * @param xLumonicMcpControlPlaneSessionId
     * @param xLumonicMcpControlPlaneAssertion
     * @param xLumonicMcpSessionToken
     * @returns ListNamespacesOutput Successful Response
     * @throws ApiError
     */
    public namespacesList(
        clusterId: string,
        requestBody: ListNamespacesInput,
        xLumonicMcpToken?: (string | null),
        xLumonicMcpUserId?: (string | null),
        xLumonicMcpClientId?: (string | null),
        xLumonicMcpControlPlaneSessionId?: (string | null),
        xLumonicMcpControlPlaneAssertion?: (string | null),
        xLumonicMcpSessionToken?: (string | null),
    ): CancelablePromise<ListNamespacesOutput> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/mcp/clusters/{cluster_id}/namespaces/list',
            path: {
                'cluster_id': clusterId,
            },
            headers: {
                'X-Lumonic-MCP-Token': xLumonicMcpToken,
                'X-Lumonic-MCP-User-Id': xLumonicMcpUserId,
                'X-Lumonic-MCP-Client-Id': xLumonicMcpClientId,
                'X-Lumonic-MCP-Control-Plane-Session-Id': xLumonicMcpControlPlaneSessionId,
                'X-Lumonic-MCP-Control-Plane-Assertion': xLumonicMcpControlPlaneAssertion,
                'X-Lumonic-MCP-Session-Token': xLumonicMcpSessionToken,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Namespace Relationships
     * List parent/child namespace relationships so the model can understand which entity types sit under or above one another before calling `entities_list_related`.
     * @param clusterId
     * @param requestBody
     * @param xLumonicMcpToken
     * @param xLumonicMcpUserId
     * @param xLumonicMcpClientId
     * @param xLumonicMcpControlPlaneSessionId
     * @param xLumonicMcpControlPlaneAssertion
     * @param xLumonicMcpSessionToken
     * @returns ListNamespaceRelationshipsOutput Successful Response
     * @throws ApiError
     */
    public namespacesListRelationships(
        clusterId: string,
        requestBody: ListNamespaceRelationshipsInput,
        xLumonicMcpToken?: (string | null),
        xLumonicMcpUserId?: (string | null),
        xLumonicMcpClientId?: (string | null),
        xLumonicMcpControlPlaneSessionId?: (string | null),
        xLumonicMcpControlPlaneAssertion?: (string | null),
        xLumonicMcpSessionToken?: (string | null),
    ): CancelablePromise<ListNamespaceRelationshipsOutput> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/mcp/clusters/{cluster_id}/namespaces/list-relationships',
            path: {
                'cluster_id': clusterId,
            },
            headers: {
                'X-Lumonic-MCP-Token': xLumonicMcpToken,
                'X-Lumonic-MCP-User-Id': xLumonicMcpUserId,
                'X-Lumonic-MCP-Client-Id': xLumonicMcpClientId,
                'X-Lumonic-MCP-Control-Plane-Session-Id': xLumonicMcpControlPlaneSessionId,
                'X-Lumonic-MCP-Control-Plane-Assertion': xLumonicMcpControlPlaneAssertion,
                'X-Lumonic-MCP-Session-Token': xLumonicMcpSessionToken,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Entities
     * List entities available in the bound cluster for downstream grounding and LX resolution.
     * @param clusterId
     * @param requestBody
     * @param xLumonicMcpToken
     * @param xLumonicMcpUserId
     * @param xLumonicMcpClientId
     * @param xLumonicMcpControlPlaneSessionId
     * @param xLumonicMcpControlPlaneAssertion
     * @param xLumonicMcpSessionToken
     * @returns ListEntitiesOutput Successful Response
     * @throws ApiError
     */
    public entitiesList(
        clusterId: string,
        requestBody: ListEntitiesInput,
        xLumonicMcpToken?: (string | null),
        xLumonicMcpUserId?: (string | null),
        xLumonicMcpClientId?: (string | null),
        xLumonicMcpControlPlaneSessionId?: (string | null),
        xLumonicMcpControlPlaneAssertion?: (string | null),
        xLumonicMcpSessionToken?: (string | null),
    ): CancelablePromise<ListEntitiesOutput> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/mcp/clusters/{cluster_id}/entities/list',
            path: {
                'cluster_id': clusterId,
            },
            headers: {
                'X-Lumonic-MCP-Token': xLumonicMcpToken,
                'X-Lumonic-MCP-User-Id': xLumonicMcpUserId,
                'X-Lumonic-MCP-Client-Id': xLumonicMcpClientId,
                'X-Lumonic-MCP-Control-Plane-Session-Id': xLumonicMcpControlPlaneSessionId,
                'X-Lumonic-MCP-Control-Plane-Assertion': xLumonicMcpControlPlaneAssertion,
                'X-Lumonic-MCP-Session-Token': xLumonicMcpSessionToken,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Related Entities
     * Traverse parent or child entity relationships from one root entity. Pass `target_namespace_id` whenever the root can have multiple child namespaces.
     * @param clusterId
     * @param requestBody
     * @param xLumonicMcpToken
     * @param xLumonicMcpUserId
     * @param xLumonicMcpClientId
     * @param xLumonicMcpControlPlaneSessionId
     * @param xLumonicMcpControlPlaneAssertion
     * @param xLumonicMcpSessionToken
     * @returns ListRelatedEntitiesOutput Successful Response
     * @throws ApiError
     */
    public entitiesListRelated(
        clusterId: string,
        requestBody: ListRelatedEntitiesInput,
        xLumonicMcpToken?: (string | null),
        xLumonicMcpUserId?: (string | null),
        xLumonicMcpClientId?: (string | null),
        xLumonicMcpControlPlaneSessionId?: (string | null),
        xLumonicMcpControlPlaneAssertion?: (string | null),
        xLumonicMcpSessionToken?: (string | null),
    ): CancelablePromise<ListRelatedEntitiesOutput> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/mcp/clusters/{cluster_id}/entities/list-related',
            path: {
                'cluster_id': clusterId,
            },
            headers: {
                'X-Lumonic-MCP-Token': xLumonicMcpToken,
                'X-Lumonic-MCP-User-Id': xLumonicMcpUserId,
                'X-Lumonic-MCP-Client-Id': xLumonicMcpClientId,
                'X-Lumonic-MCP-Control-Plane-Session-Id': xLumonicMcpControlPlaneSessionId,
                'X-Lumonic-MCP-Control-Plane-Assertion': xLumonicMcpControlPlaneAssertion,
                'X-Lumonic-MCP-Session-Token': xLumonicMcpSessionToken,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Dimensions
     * Read the effective scenarios, periods, defaults, and resampling guidance for a cluster, namespace, or entity scope.
     * @param clusterId
     * @param requestBody
     * @param xLumonicMcpToken
     * @param xLumonicMcpUserId
     * @param xLumonicMcpClientId
     * @param xLumonicMcpControlPlaneSessionId
     * @param xLumonicMcpControlPlaneAssertion
     * @param xLumonicMcpSessionToken
     * @returns ListDimensionsOutput Successful Response
     * @throws ApiError
     */
    public dimensionsList(
        clusterId: string,
        requestBody: ListDimensionsInput,
        xLumonicMcpToken?: (string | null),
        xLumonicMcpUserId?: (string | null),
        xLumonicMcpClientId?: (string | null),
        xLumonicMcpControlPlaneSessionId?: (string | null),
        xLumonicMcpControlPlaneAssertion?: (string | null),
        xLumonicMcpSessionToken?: (string | null),
    ): CancelablePromise<ListDimensionsOutput> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/mcp/clusters/{cluster_id}/dimensions/list',
            path: {
                'cluster_id': clusterId,
            },
            headers: {
                'X-Lumonic-MCP-Token': xLumonicMcpToken,
                'X-Lumonic-MCP-User-Id': xLumonicMcpUserId,
                'X-Lumonic-MCP-Client-Id': xLumonicMcpClientId,
                'X-Lumonic-MCP-Control-Plane-Session-Id': xLumonicMcpControlPlaneSessionId,
                'X-Lumonic-MCP-Control-Plane-Assertion': xLumonicMcpControlPlaneAssertion,
                'X-Lumonic-MCP-Session-Token': xLumonicMcpSessionToken,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Dashboards
     * List dashboards in the bound cluster so saved dashboard views can be rendered through MCP.
     * @param clusterId
     * @param requestBody
     * @param xLumonicMcpToken
     * @param xLumonicMcpUserId
     * @param xLumonicMcpClientId
     * @param xLumonicMcpControlPlaneSessionId
     * @param xLumonicMcpControlPlaneAssertion
     * @param xLumonicMcpSessionToken
     * @returns ListDashboardsOutput Successful Response
     * @throws ApiError
     */
    public dashboardsList(
        clusterId: string,
        requestBody: ListDashboardsInput,
        xLumonicMcpToken?: (string | null),
        xLumonicMcpUserId?: (string | null),
        xLumonicMcpClientId?: (string | null),
        xLumonicMcpControlPlaneSessionId?: (string | null),
        xLumonicMcpControlPlaneAssertion?: (string | null),
        xLumonicMcpSessionToken?: (string | null),
    ): CancelablePromise<ListDashboardsOutput> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/mcp/clusters/{cluster_id}/dashboards/list',
            path: {
                'cluster_id': clusterId,
            },
            headers: {
                'X-Lumonic-MCP-Token': xLumonicMcpToken,
                'X-Lumonic-MCP-User-Id': xLumonicMcpUserId,
                'X-Lumonic-MCP-Client-Id': xLumonicMcpClientId,
                'X-Lumonic-MCP-Control-Plane-Session-Id': xLumonicMcpControlPlaneSessionId,
                'X-Lumonic-MCP-Control-Plane-Assertion': xLumonicMcpControlPlaneAssertion,
                'X-Lumonic-MCP-Session-Token': xLumonicMcpSessionToken,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Read Dashboard
     * Read one dashboard and expose widget ids, saved view ids, compact LX expressions, and suggested views_render_table(view_id=...) calls for the underlying data behind cards, tables, and charts.
     * @param clusterId
     * @param requestBody
     * @param xLumonicMcpToken
     * @param xLumonicMcpUserId
     * @param xLumonicMcpClientId
     * @param xLumonicMcpControlPlaneSessionId
     * @param xLumonicMcpControlPlaneAssertion
     * @param xLumonicMcpSessionToken
     * @returns ReadDashboardOutput Successful Response
     * @throws ApiError
     */
    public dashboardsRead(
        clusterId: string,
        requestBody: ReadDashboardInput,
        xLumonicMcpToken?: (string | null),
        xLumonicMcpUserId?: (string | null),
        xLumonicMcpClientId?: (string | null),
        xLumonicMcpControlPlaneSessionId?: (string | null),
        xLumonicMcpControlPlaneAssertion?: (string | null),
        xLumonicMcpSessionToken?: (string | null),
    ): CancelablePromise<ReadDashboardOutput> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/mcp/clusters/{cluster_id}/dashboards/read',
            path: {
                'cluster_id': clusterId,
            },
            headers: {
                'X-Lumonic-MCP-Token': xLumonicMcpToken,
                'X-Lumonic-MCP-User-Id': xLumonicMcpUserId,
                'X-Lumonic-MCP-Client-Id': xLumonicMcpClientId,
                'X-Lumonic-MCP-Control-Plane-Session-Id': xLumonicMcpControlPlaneSessionId,
                'X-Lumonic-MCP-Control-Plane-Assertion': xLumonicMcpControlPlaneAssertion,
                'X-Lumonic-MCP-Session-Token': xLumonicMcpSessionToken,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Covenant Reporting Statuses
     * Read the latest covenant reporting statuses across a borrower namespace. For each borrower/covenant, the tool uses Lumonic covenant source data and formula-backed columns, picks the most recent covenant period on or before as_of_date, and applies the requested compliance status filter. By default it returns OUT_OF_COMPLIANCE rows for level-1 covenant reporting: borrower, covenant name, required threshold, actual value, period, and all same-period covenant column values.
     * @param clusterId
     * @param requestBody
     * @param xLumonicMcpToken
     * @param xLumonicMcpUserId
     * @param xLumonicMcpClientId
     * @param xLumonicMcpControlPlaneSessionId
     * @param xLumonicMcpControlPlaneAssertion
     * @param xLumonicMcpSessionToken
     * @returns CovenantReportingStatusesOutput Successful Response
     * @throws ApiError
     */
    public covenantsReportingStatuses(
        clusterId: string,
        requestBody: CovenantReportingStatusesInput,
        xLumonicMcpToken?: (string | null),
        xLumonicMcpUserId?: (string | null),
        xLumonicMcpClientId?: (string | null),
        xLumonicMcpControlPlaneSessionId?: (string | null),
        xLumonicMcpControlPlaneAssertion?: (string | null),
        xLumonicMcpSessionToken?: (string | null),
    ): CancelablePromise<CovenantReportingStatusesOutput> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/mcp/clusters/{cluster_id}/covenants/reporting-statuses',
            path: {
                'cluster_id': clusterId,
            },
            headers: {
                'X-Lumonic-MCP-Token': xLumonicMcpToken,
                'X-Lumonic-MCP-User-Id': xLumonicMcpUserId,
                'X-Lumonic-MCP-Client-Id': xLumonicMcpClientId,
                'X-Lumonic-MCP-Control-Plane-Session-Id': xLumonicMcpControlPlaneSessionId,
                'X-Lumonic-MCP-Control-Plane-Assertion': xLumonicMcpControlPlaneAssertion,
                'X-Lumonic-MCP-Session-Token': xLumonicMcpSessionToken,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Schema Groups
     * List valid field groups for a namespace or entity scope. Use before building workbook field catalogs or passing group_name/group_id into schema_list_properties or discovery_search_properties.
     * @param clusterId
     * @param requestBody
     * @param xLumonicMcpToken
     * @param xLumonicMcpUserId
     * @param xLumonicMcpClientId
     * @param xLumonicMcpControlPlaneSessionId
     * @param xLumonicMcpControlPlaneAssertion
     * @param xLumonicMcpSessionToken
     * @returns ListSchemaGroupsOutput Successful Response
     * @throws ApiError
     */
    public schemaGroupsList(
        clusterId: string,
        requestBody: ListSchemaGroupsInput,
        xLumonicMcpToken?: (string | null),
        xLumonicMcpUserId?: (string | null),
        xLumonicMcpClientId?: (string | null),
        xLumonicMcpControlPlaneSessionId?: (string | null),
        xLumonicMcpControlPlaneAssertion?: (string | null),
        xLumonicMcpSessionToken?: (string | null),
    ): CancelablePromise<ListSchemaGroupsOutput> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/mcp/clusters/{cluster_id}/schema/groups/list',
            path: {
                'cluster_id': clusterId,
            },
            headers: {
                'X-Lumonic-MCP-Token': xLumonicMcpToken,
                'X-Lumonic-MCP-User-Id': xLumonicMcpUserId,
                'X-Lumonic-MCP-Client-Id': xLumonicMcpClientId,
                'X-Lumonic-MCP-Control-Plane-Session-Id': xLumonicMcpControlPlaneSessionId,
                'X-Lumonic-MCP-Control-Plane-Assertion': xLumonicMcpControlPlaneAssertion,
                'X-Lumonic-MCP-Session-Token': xLumonicMcpSessionToken,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Schema Properties
     * Return a deterministic shortlist of schema properties for exact pointer grounding. Use `sort=prevalence` to find high-coverage fields, `is_global` and `property_types` to narrow noisy searches, and `detail_level=full` only when you need richer metadata.
     * @param clusterId
     * @param requestBody
     * @param xLumonicMcpToken
     * @param xLumonicMcpUserId
     * @param xLumonicMcpClientId
     * @param xLumonicMcpControlPlaneSessionId
     * @param xLumonicMcpControlPlaneAssertion
     * @param xLumonicMcpSessionToken
     * @returns ListSchemaPropertiesOutput Successful Response
     * @throws ApiError
     */
    public schemaListProperties(
        clusterId: string,
        requestBody: ListSchemaPropertiesInput,
        xLumonicMcpToken?: (string | null),
        xLumonicMcpUserId?: (string | null),
        xLumonicMcpClientId?: (string | null),
        xLumonicMcpControlPlaneSessionId?: (string | null),
        xLumonicMcpControlPlaneAssertion?: (string | null),
        xLumonicMcpSessionToken?: (string | null),
    ): CancelablePromise<ListSchemaPropertiesOutput> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/mcp/clusters/{cluster_id}/schema/list-properties',
            path: {
                'cluster_id': clusterId,
            },
            headers: {
                'X-Lumonic-MCP-Token': xLumonicMcpToken,
                'X-Lumonic-MCP-User-Id': xLumonicMcpUserId,
                'X-Lumonic-MCP-Client-Id': xLumonicMcpClientId,
                'X-Lumonic-MCP-Control-Plane-Session-Id': xLumonicMcpControlPlaneSessionId,
                'X-Lumonic-MCP-Control-Plane-Assertion': xLumonicMcpControlPlaneAssertion,
                'X-Lumonic-MCP-Session-Token': xLumonicMcpSessionToken,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Search Properties
     * Return a shortlist of likely property pointers for a business concept. Use `is_global` and `property_types` to avoid line-item noise before LX.
     * @param clusterId
     * @param requestBody
     * @param xLumonicMcpToken
     * @param xLumonicMcpUserId
     * @param xLumonicMcpClientId
     * @param xLumonicMcpControlPlaneSessionId
     * @param xLumonicMcpControlPlaneAssertion
     * @param xLumonicMcpSessionToken
     * @returns SearchPropertiesOutput Successful Response
     * @throws ApiError
     */
    public discoverySearchProperties(
        clusterId: string,
        requestBody: SearchPropertiesInput,
        xLumonicMcpToken?: (string | null),
        xLumonicMcpUserId?: (string | null),
        xLumonicMcpClientId?: (string | null),
        xLumonicMcpControlPlaneSessionId?: (string | null),
        xLumonicMcpControlPlaneAssertion?: (string | null),
        xLumonicMcpSessionToken?: (string | null),
    ): CancelablePromise<SearchPropertiesOutput> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/mcp/clusters/{cluster_id}/discovery/search-properties',
            path: {
                'cluster_id': clusterId,
            },
            headers: {
                'X-Lumonic-MCP-Token': xLumonicMcpToken,
                'X-Lumonic-MCP-User-Id': xLumonicMcpUserId,
                'X-Lumonic-MCP-Client-Id': xLumonicMcpClientId,
                'X-Lumonic-MCP-Control-Plane-Session-Id': xLumonicMcpControlPlaneSessionId,
                'X-Lumonic-MCP-Control-Plane-Assertion': xLumonicMcpControlPlaneAssertion,
                'X-Lumonic-MCP-Session-Token': xLumonicMcpSessionToken,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Resources
     * List default Lumonic MCP resources available through the tool-compatible resource wrapper. Use `resources_read` with one returned URI to fetch resource text, including the cached cluster workspace summary.
     * @param clusterId
     * @param requestBody
     * @param xLumonicMcpToken
     * @param xLumonicMcpUserId
     * @param xLumonicMcpClientId
     * @param xLumonicMcpControlPlaneSessionId
     * @param xLumonicMcpControlPlaneAssertion
     * @param xLumonicMcpSessionToken
     * @returns ListResourcesOutput Successful Response
     * @throws ApiError
     */
    public resourcesList(
        clusterId: string,
        requestBody: ListResourcesInput,
        xLumonicMcpToken?: (string | null),
        xLumonicMcpUserId?: (string | null),
        xLumonicMcpClientId?: (string | null),
        xLumonicMcpControlPlaneSessionId?: (string | null),
        xLumonicMcpControlPlaneAssertion?: (string | null),
        xLumonicMcpSessionToken?: (string | null),
    ): CancelablePromise<ListResourcesOutput> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/mcp/clusters/{cluster_id}/resources/list',
            path: {
                'cluster_id': clusterId,
            },
            headers: {
                'X-Lumonic-MCP-Token': xLumonicMcpToken,
                'X-Lumonic-MCP-User-Id': xLumonicMcpUserId,
                'X-Lumonic-MCP-Client-Id': xLumonicMcpClientId,
                'X-Lumonic-MCP-Control-Plane-Session-Id': xLumonicMcpControlPlaneSessionId,
                'X-Lumonic-MCP-Control-Plane-Assertion': xLumonicMcpControlPlaneAssertion,
                'X-Lumonic-MCP-Session-Token': xLumonicMcpSessionToken,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Read Resource
     * Read one known Lumonic MCP resource by URI. Chat-style clients that cannot browse native MCP resources should call this tool to fetch resource text such as `lumonic://cluster/workspace-summary`. This can also read explicitly requested hidden documentation resources such as `lumonic://mcp/tool-catalog`; call `resources_list` first for the default resource set.
     * @param clusterId
     * @param requestBody
     * @param xLumonicMcpToken
     * @param xLumonicMcpUserId
     * @param xLumonicMcpClientId
     * @param xLumonicMcpControlPlaneSessionId
     * @param xLumonicMcpControlPlaneAssertion
     * @param xLumonicMcpSessionToken
     * @returns ReadResourceOutput Successful Response
     * @throws ApiError
     */
    public resourcesRead(
        clusterId: string,
        requestBody: ReadResourceInput,
        xLumonicMcpToken?: (string | null),
        xLumonicMcpUserId?: (string | null),
        xLumonicMcpClientId?: (string | null),
        xLumonicMcpControlPlaneSessionId?: (string | null),
        xLumonicMcpControlPlaneAssertion?: (string | null),
        xLumonicMcpSessionToken?: (string | null),
    ): CancelablePromise<ReadResourceOutput> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/mcp/clusters/{cluster_id}/resources/read',
            path: {
                'cluster_id': clusterId,
            },
            headers: {
                'X-Lumonic-MCP-Token': xLumonicMcpToken,
                'X-Lumonic-MCP-User-Id': xLumonicMcpUserId,
                'X-Lumonic-MCP-Client-Id': xLumonicMcpClientId,
                'X-Lumonic-MCP-Control-Plane-Session-Id': xLumonicMcpControlPlaneSessionId,
                'X-Lumonic-MCP-Control-Plane-Assertion': xLumonicMcpControlPlaneAssertion,
                'X-Lumonic-MCP-Session-Token': xLumonicMcpSessionToken,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Validate LX
     * Validate one or more LX expressions against explicit entity ids in the bound cluster. Call `entities_list` or `entities_list_related` first when entity ids are not already known.
     * @param clusterId
     * @param requestBody
     * @param xLumonicMcpToken
     * @param xLumonicMcpUserId
     * @param xLumonicMcpClientId
     * @param xLumonicMcpControlPlaneSessionId
     * @param xLumonicMcpControlPlaneAssertion
     * @param xLumonicMcpSessionToken
     * @returns ValidateLxOutput Successful Response
     * @throws ApiError
     */
    public lxValidate(
        clusterId: string,
        requestBody: ValidateLxInput,
        xLumonicMcpToken?: (string | null),
        xLumonicMcpUserId?: (string | null),
        xLumonicMcpClientId?: (string | null),
        xLumonicMcpControlPlaneSessionId?: (string | null),
        xLumonicMcpControlPlaneAssertion?: (string | null),
        xLumonicMcpSessionToken?: (string | null),
    ): CancelablePromise<ValidateLxOutput> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/mcp/clusters/{cluster_id}/lx/validate',
            path: {
                'cluster_id': clusterId,
            },
            headers: {
                'X-Lumonic-MCP-Token': xLumonicMcpToken,
                'X-Lumonic-MCP-User-Id': xLumonicMcpUserId,
                'X-Lumonic-MCP-Client-Id': xLumonicMcpClientId,
                'X-Lumonic-MCP-Control-Plane-Session-Id': xLumonicMcpControlPlaneSessionId,
                'X-Lumonic-MCP-Control-Plane-Assertion': xLumonicMcpControlPlaneAssertion,
                'X-Lumonic-MCP-Session-Token': xLumonicMcpSessionToken,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Resolve LX
     * Resolve one or more LX expressions against explicit entity ids in the bound cluster. Use this for direct value evaluation, including direct cashflow expression reads, when entity ids are already known. For ranked lists, filters, groups, totals, time-series tables, portfolio screens, or cashflow performance metrics, use `views_render_table` instead so the dashboard view layer owns table semantics. Call `entities_list` or `entities_list_related` first when entity ids are not already known; use `response_shape="wide_table"` plus `entity_limit`/`entity_offset` for multi-metric entity tables; wide-table latest/exact reads support up to 500 entities, while range reads are capped at 50 entities; for snapshot/as-of/current asks use `mode="latest"` or `mode="exact"` and treat returned dates as source provenance; for trend/history/over-time asks use `mode="range"` and treat returned dates as the time axis; effective dates are returned by default; set `include_dates=false` only for compact bulk reads where dates will not be displayed or used. Use `lx_traceback_url` to build an authenticated app traceback link for a specific resolved cell.
     * @param clusterId
     * @param requestBody
     * @param xLumonicMcpToken
     * @param xLumonicMcpUserId
     * @param xLumonicMcpClientId
     * @param xLumonicMcpControlPlaneSessionId
     * @param xLumonicMcpControlPlaneAssertion
     * @param xLumonicMcpSessionToken
     * @returns ResolveLxOutput Successful Response
     * @throws ApiError
     */
    public lxResolve(
        clusterId: string,
        requestBody: ResolveLxInput,
        xLumonicMcpToken?: (string | null),
        xLumonicMcpUserId?: (string | null),
        xLumonicMcpClientId?: (string | null),
        xLumonicMcpControlPlaneSessionId?: (string | null),
        xLumonicMcpControlPlaneAssertion?: (string | null),
        xLumonicMcpSessionToken?: (string | null),
    ): CancelablePromise<ResolveLxOutput> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/mcp/clusters/{cluster_id}/lx/resolve',
            path: {
                'cluster_id': clusterId,
            },
            headers: {
                'X-Lumonic-MCP-Token': xLumonicMcpToken,
                'X-Lumonic-MCP-User-Id': xLumonicMcpUserId,
                'X-Lumonic-MCP-Client-Id': xLumonicMcpClientId,
                'X-Lumonic-MCP-Control-Plane-Session-Id': xLumonicMcpControlPlaneSessionId,
                'X-Lumonic-MCP-Control-Plane-Assertion': xLumonicMcpControlPlaneAssertion,
                'X-Lumonic-MCP-Session-Token': xLumonicMcpSessionToken,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Build LX Traceback URL
     * Build an authenticated Lumonic app traceback URL for one resolved LX cell. Prefer passing `effective_date` from a prior `lx_resolve` response: `row.date` for table output, or `wide_table.rows[].dates[query_id]` for wide_table output. If `effective_date` is omitted, this tool performs a tiny latest-mode resolve for the single entity/expression to infer the effective date before building the URL.
     * @param clusterId
     * @param requestBody
     * @param xLumonicMcpToken
     * @param xLumonicMcpUserId
     * @param xLumonicMcpClientId
     * @param xLumonicMcpControlPlaneSessionId
     * @param xLumonicMcpControlPlaneAssertion
     * @param xLumonicMcpSessionToken
     * @returns BuildTracebackUrlOutput Successful Response
     * @throws ApiError
     */
    public lxTracebackUrl(
        clusterId: string,
        requestBody: BuildTracebackUrlInput,
        xLumonicMcpToken?: (string | null),
        xLumonicMcpUserId?: (string | null),
        xLumonicMcpClientId?: (string | null),
        xLumonicMcpControlPlaneSessionId?: (string | null),
        xLumonicMcpControlPlaneAssertion?: (string | null),
        xLumonicMcpSessionToken?: (string | null),
    ): CancelablePromise<BuildTracebackUrlOutput> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/mcp/clusters/{cluster_id}/lx/traceback-url',
            path: {
                'cluster_id': clusterId,
            },
            headers: {
                'X-Lumonic-MCP-Token': xLumonicMcpToken,
                'X-Lumonic-MCP-User-Id': xLumonicMcpUserId,
                'X-Lumonic-MCP-Client-Id': xLumonicMcpClientId,
                'X-Lumonic-MCP-Control-Plane-Session-Id': xLumonicMcpControlPlaneSessionId,
                'X-Lumonic-MCP-Control-Plane-Assertion': xLumonicMcpControlPlaneAssertion,
                'X-Lumonic-MCP-Session-Token': xLumonicMcpSessionToken,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * View Table Query Guide
     * Read a short guide for choosing between `views_render_table` and `lx_resolve`. Use this when the client does not expose MCP prompts/resources well.
     * @param clusterId
     * @param requestBody
     * @param xLumonicMcpToken
     * @param xLumonicMcpUserId
     * @param xLumonicMcpClientId
     * @param xLumonicMcpControlPlaneSessionId
     * @param xLumonicMcpControlPlaneAssertion
     * @param xLumonicMcpSessionToken
     * @returns TableQueryGuideOutput Successful Response
     * @throws ApiError
     */
    public viewsTableQueryGuide(
        clusterId: string,
        requestBody: TableQueryGuideInput,
        xLumonicMcpToken?: (string | null),
        xLumonicMcpUserId?: (string | null),
        xLumonicMcpClientId?: (string | null),
        xLumonicMcpControlPlaneSessionId?: (string | null),
        xLumonicMcpControlPlaneAssertion?: (string | null),
        xLumonicMcpSessionToken?: (string | null),
    ): CancelablePromise<TableQueryGuideOutput> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/mcp/clusters/{cluster_id}/views/table-query-guide',
            path: {
                'cluster_id': clusterId,
            },
            headers: {
                'X-Lumonic-MCP-Token': xLumonicMcpToken,
                'X-Lumonic-MCP-User-Id': xLumonicMcpUserId,
                'X-Lumonic-MCP-Client-Id': xLumonicMcpClientId,
                'X-Lumonic-MCP-Control-Plane-Session-Id': xLumonicMcpControlPlaneSessionId,
                'X-Lumonic-MCP-Control-Plane-Assertion': xLumonicMcpControlPlaneAssertion,
                'X-Lumonic-MCP-Session-Token': xLumonicMcpSessionToken,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Render View Table
     * Render table-shaped analytical reads through the same dashboard view layer as the Lumonic app. Use this for portfolio screens, top-N/ranked lists, filters, grouped rollups, totals, snapshot tables, time-series tables, and cashflow performance metrics. Do not use `lx_resolve` for these table-query asks; `lx_resolve` is for direct LX value evaluation across known entity ids, including direct cashflow expression reads. Cashflow table metrics materialize inside the view renderer automatically from property metadata.
     * @param clusterId
     * @param requestBody
     * @param xLumonicMcpToken
     * @param xLumonicMcpUserId
     * @param xLumonicMcpClientId
     * @param xLumonicMcpControlPlaneSessionId
     * @param xLumonicMcpControlPlaneAssertion
     * @param xLumonicMcpSessionToken
     * @returns RenderTableOutput Successful Response
     * @throws ApiError
     */
    public viewsRenderTable(
        clusterId: string,
        requestBody: RenderTableInput,
        xLumonicMcpToken?: (string | null),
        xLumonicMcpUserId?: (string | null),
        xLumonicMcpClientId?: (string | null),
        xLumonicMcpControlPlaneSessionId?: (string | null),
        xLumonicMcpControlPlaneAssertion?: (string | null),
        xLumonicMcpSessionToken?: (string | null),
    ): CancelablePromise<RenderTableOutput> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/mcp/clusters/{cluster_id}/views/render-table',
            path: {
                'cluster_id': clusterId,
            },
            headers: {
                'X-Lumonic-MCP-Token': xLumonicMcpToken,
                'X-Lumonic-MCP-User-Id': xLumonicMcpUserId,
                'X-Lumonic-MCP-Client-Id': xLumonicMcpClientId,
                'X-Lumonic-MCP-Control-Plane-Session-Id': xLumonicMcpControlPlaneSessionId,
                'X-Lumonic-MCP-Control-Plane-Assertion': xLumonicMcpControlPlaneAssertion,
                'X-Lumonic-MCP-Session-Token': xLumonicMcpSessionToken,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Excel Functions
     * List Lumonic Excel custom functions for workbook generation. Use this when the output should be refreshable Excel formulas rather than resolved data. Resource: `lumonic://excel/functions`.
     * @param clusterId
     * @param requestBody
     * @param xLumonicMcpToken
     * @param xLumonicMcpUserId
     * @param xLumonicMcpClientId
     * @param xLumonicMcpControlPlaneSessionId
     * @param xLumonicMcpControlPlaneAssertion
     * @param xLumonicMcpSessionToken
     * @returns ListExcelFunctionsOutput Successful Response
     * @throws ApiError
     */
    public excelFunctionsList(
        clusterId: string,
        requestBody: ListExcelFunctionsInput,
        xLumonicMcpToken?: (string | null),
        xLumonicMcpUserId?: (string | null),
        xLumonicMcpClientId?: (string | null),
        xLumonicMcpControlPlaneSessionId?: (string | null),
        xLumonicMcpControlPlaneAssertion?: (string | null),
        xLumonicMcpSessionToken?: (string | null),
    ): CancelablePromise<ListExcelFunctionsOutput> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/mcp/clusters/{cluster_id}/excel/functions/list',
            path: {
                'cluster_id': clusterId,
            },
            headers: {
                'X-Lumonic-MCP-Token': xLumonicMcpToken,
                'X-Lumonic-MCP-User-Id': xLumonicMcpUserId,
                'X-Lumonic-MCP-Client-Id': xLumonicMcpClientId,
                'X-Lumonic-MCP-Control-Plane-Session-Id': xLumonicMcpControlPlaneSessionId,
                'X-Lumonic-MCP-Control-Plane-Assertion': xLumonicMcpControlPlaneAssertion,
                'X-Lumonic-MCP-Session-Token': xLumonicMcpSessionToken,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Validate Excel Formulas
     * Statically validate generated Lumonic Excel formulas against the current function catalog. This validates workbook formula shape, not business data values, cluster runtime semantics, or namespace token casing/pluralization. Any implicit-intersection `@` before an LX function is invalid.
     * @param clusterId
     * @param requestBody
     * @param xLumonicMcpToken
     * @param xLumonicMcpUserId
     * @param xLumonicMcpClientId
     * @param xLumonicMcpControlPlaneSessionId
     * @param xLumonicMcpControlPlaneAssertion
     * @param xLumonicMcpSessionToken
     * @returns ValidateExcelFormulasOutput Successful Response
     * @throws ApiError
     */
    public excelFormulasValidate(
        clusterId: string,
        requestBody: ValidateExcelFormulasInput,
        xLumonicMcpToken?: (string | null),
        xLumonicMcpUserId?: (string | null),
        xLumonicMcpClientId?: (string | null),
        xLumonicMcpControlPlaneSessionId?: (string | null),
        xLumonicMcpControlPlaneAssertion?: (string | null),
        xLumonicMcpSessionToken?: (string | null),
    ): CancelablePromise<ValidateExcelFormulasOutput> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/mcp/clusters/{cluster_id}/excel/formulas/validate',
            path: {
                'cluster_id': clusterId,
            },
            headers: {
                'X-Lumonic-MCP-Token': xLumonicMcpToken,
                'X-Lumonic-MCP-User-Id': xLumonicMcpUserId,
                'X-Lumonic-MCP-Client-Id': xLumonicMcpClientId,
                'X-Lumonic-MCP-Control-Plane-Session-Id': xLumonicMcpControlPlaneSessionId,
                'X-Lumonic-MCP-Control-Plane-Assertion': xLumonicMcpControlPlaneAssertion,
                'X-Lumonic-MCP-Session-Token': xLumonicMcpSessionToken,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Cluster Team Users
     * List users with cluster-scoped membership — i.e. the internal team that owns this cluster (for lender clusters, the lender's own employees). Use this to answer "who is on my team" questions or to look up email addresses for people at the cluster-owning organization. Does not return borrower-portal users; use `users_list_entity_team` for entity-scoped memberships.
     * @param clusterId
     * @param requestBody
     * @param xLumonicMcpToken
     * @param xLumonicMcpUserId
     * @param xLumonicMcpClientId
     * @param xLumonicMcpControlPlaneSessionId
     * @param xLumonicMcpControlPlaneAssertion
     * @param xLumonicMcpSessionToken
     * @returns ListClusterTeamOutput Successful Response
     * @throws ApiError
     */
    public usersListClusterTeam(
        clusterId: string,
        requestBody: ListClusterTeamInput,
        xLumonicMcpToken?: (string | null),
        xLumonicMcpUserId?: (string | null),
        xLumonicMcpClientId?: (string | null),
        xLumonicMcpControlPlaneSessionId?: (string | null),
        xLumonicMcpControlPlaneAssertion?: (string | null),
        xLumonicMcpSessionToken?: (string | null),
    ): CancelablePromise<ListClusterTeamOutput> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/mcp/clusters/{cluster_id}/users/list-cluster-team',
            path: {
                'cluster_id': clusterId,
            },
            headers: {
                'X-Lumonic-MCP-Token': xLumonicMcpToken,
                'X-Lumonic-MCP-User-Id': xLumonicMcpUserId,
                'X-Lumonic-MCP-Client-Id': xLumonicMcpClientId,
                'X-Lumonic-MCP-Control-Plane-Session-Id': xLumonicMcpControlPlaneSessionId,
                'X-Lumonic-MCP-Control-Plane-Assertion': xLumonicMcpControlPlaneAssertion,
                'X-Lumonic-MCP-Session-Token': xLumonicMcpSessionToken,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Entity Team Users
     * List users with membership scoped to a specific entity. For lender clusters this is typically the borrower's own portal users (CFO, controller, finance team at the portfolio company). Use this to look up contact information for people at a specific borrower/entity. Does not return the cluster owner's internal team; use `users_list_cluster_team` for that.
     * @param clusterId
     * @param requestBody
     * @param xLumonicMcpToken
     * @param xLumonicMcpUserId
     * @param xLumonicMcpClientId
     * @param xLumonicMcpControlPlaneSessionId
     * @param xLumonicMcpControlPlaneAssertion
     * @param xLumonicMcpSessionToken
     * @returns ListEntityTeamOutput Successful Response
     * @throws ApiError
     */
    public usersListEntityTeam(
        clusterId: string,
        requestBody: ListEntityTeamInput,
        xLumonicMcpToken?: (string | null),
        xLumonicMcpUserId?: (string | null),
        xLumonicMcpClientId?: (string | null),
        xLumonicMcpControlPlaneSessionId?: (string | null),
        xLumonicMcpControlPlaneAssertion?: (string | null),
        xLumonicMcpSessionToken?: (string | null),
    ): CancelablePromise<ListEntityTeamOutput> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/mcp/clusters/{cluster_id}/users/list-entity-team',
            path: {
                'cluster_id': clusterId,
            },
            headers: {
                'X-Lumonic-MCP-Token': xLumonicMcpToken,
                'X-Lumonic-MCP-User-Id': xLumonicMcpUserId,
                'X-Lumonic-MCP-Client-Id': xLumonicMcpClientId,
                'X-Lumonic-MCP-Control-Plane-Session-Id': xLumonicMcpControlPlaneSessionId,
                'X-Lumonic-MCP-Control-Plane-Assertion': xLumonicMcpControlPlaneAssertion,
                'X-Lumonic-MCP-Session-Token': xLumonicMcpSessionToken,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Reporting Entity Status
     * Read deliverable submission status for one entity as of a snapshot date. Uses due-date status view and ignores completions after as_of_date. Returns a summary block first, then compact rows by default; raise limit or page with offset/next_offset for more rows, and set detail_level=full for reason sentences and task ids. This v1 reporting domain does not evaluate covenant compliance.
     * @param clusterId
     * @param requestBody
     * @param xLumonicMcpToken
     * @param xLumonicMcpUserId
     * @param xLumonicMcpClientId
     * @param xLumonicMcpControlPlaneSessionId
     * @param xLumonicMcpControlPlaneAssertion
     * @param xLumonicMcpSessionToken
     * @returns ReportingEntityStatusOutput Successful Response
     * @throws ApiError
     */
    public reportingEntityStatus(
        clusterId: string,
        requestBody: ReportingEntityStatusInput,
        xLumonicMcpToken?: (string | null),
        xLumonicMcpUserId?: (string | null),
        xLumonicMcpClientId?: (string | null),
        xLumonicMcpControlPlaneSessionId?: (string | null),
        xLumonicMcpControlPlaneAssertion?: (string | null),
        xLumonicMcpSessionToken?: (string | null),
    ): CancelablePromise<ReportingEntityStatusOutput> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/mcp/clusters/{cluster_id}/reporting/entity-status',
            path: {
                'cluster_id': clusterId,
            },
            headers: {
                'X-Lumonic-MCP-Token': xLumonicMcpToken,
                'X-Lumonic-MCP-User-Id': xLumonicMcpUserId,
                'X-Lumonic-MCP-Client-Id': xLumonicMcpClientId,
                'X-Lumonic-MCP-Control-Plane-Session-Id': xLumonicMcpControlPlaneSessionId,
                'X-Lumonic-MCP-Control-Plane-Assertion': xLumonicMcpControlPlaneAssertion,
                'X-Lumonic-MCP-Session-Token': xLumonicMcpSessionToken,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Reporting Period Summary
     * Summarize deliverable submission status across a namespace for a due-date window as of a snapshot date. Returns a summary block (state and per-entity counts over the full filtered set) first, then compact rows by default; raise limit or page with offset/next_offset for more rows, and set detail_level=full for reason sentences and task ids. Covenant compliance is out of scope for v1.
     * @param clusterId
     * @param requestBody
     * @param xLumonicMcpToken
     * @param xLumonicMcpUserId
     * @param xLumonicMcpClientId
     * @param xLumonicMcpControlPlaneSessionId
     * @param xLumonicMcpControlPlaneAssertion
     * @param xLumonicMcpSessionToken
     * @returns ReportingPeriodSummaryOutput Successful Response
     * @throws ApiError
     */
    public reportingPeriodSummary(
        clusterId: string,
        requestBody: ReportingPeriodSummaryInput,
        xLumonicMcpToken?: (string | null),
        xLumonicMcpUserId?: (string | null),
        xLumonicMcpClientId?: (string | null),
        xLumonicMcpControlPlaneSessionId?: (string | null),
        xLumonicMcpControlPlaneAssertion?: (string | null),
        xLumonicMcpSessionToken?: (string | null),
    ): CancelablePromise<ReportingPeriodSummaryOutput> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/mcp/clusters/{cluster_id}/reporting/period-summary',
            path: {
                'cluster_id': clusterId,
            },
            headers: {
                'X-Lumonic-MCP-Token': xLumonicMcpToken,
                'X-Lumonic-MCP-User-Id': xLumonicMcpUserId,
                'X-Lumonic-MCP-Client-Id': xLumonicMcpClientId,
                'X-Lumonic-MCP-Control-Plane-Session-Id': xLumonicMcpControlPlaneSessionId,
                'X-Lumonic-MCP-Control-Plane-Assertion': xLumonicMcpControlPlaneAssertion,
                'X-Lumonic-MCP-Session-Token': xLumonicMcpSessionToken,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Reporting Reminder Targets
     * Resolve internal deal-team reminder recipients for missing reporting deliverables. Returns a summary block first, then compact status rows plus recipients deduplicated per entity (recipients_by_entity); raise limit or page with offset/next_offset for more rows, and set detail_level=full for reason sentences and task ids. This read-only tool never sends Slack, email, or borrower-portal notifications.
     * @param clusterId
     * @param requestBody
     * @param xLumonicMcpToken
     * @param xLumonicMcpUserId
     * @param xLumonicMcpClientId
     * @param xLumonicMcpControlPlaneSessionId
     * @param xLumonicMcpControlPlaneAssertion
     * @param xLumonicMcpSessionToken
     * @returns ReportingReminderTargetsOutput Successful Response
     * @throws ApiError
     */
    public reportingReminderTargets(
        clusterId: string,
        requestBody: ReportingReminderTargetsInput,
        xLumonicMcpToken?: (string | null),
        xLumonicMcpUserId?: (string | null),
        xLumonicMcpClientId?: (string | null),
        xLumonicMcpControlPlaneSessionId?: (string | null),
        xLumonicMcpControlPlaneAssertion?: (string | null),
        xLumonicMcpSessionToken?: (string | null),
    ): CancelablePromise<ReportingReminderTargetsOutput> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/mcp/clusters/{cluster_id}/reporting/reminder-targets',
            path: {
                'cluster_id': clusterId,
            },
            headers: {
                'X-Lumonic-MCP-Token': xLumonicMcpToken,
                'X-Lumonic-MCP-User-Id': xLumonicMcpUserId,
                'X-Lumonic-MCP-Client-Id': xLumonicMcpClientId,
                'X-Lumonic-MCP-Control-Plane-Session-Id': xLumonicMcpControlPlaneSessionId,
                'X-Lumonic-MCP-Control-Plane-Assertion': xLumonicMcpControlPlaneAssertion,
                'X-Lumonic-MCP-Session-Token': xLumonicMcpSessionToken,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

}
